import { create } from 'zustand';

import { Wallet, WalletMap } from '@/types';
import { generateWallet } from '@utils/crypto';
import { getFromLocalStorage, getFromSessionStorage, setToLocalStorage, setToSessionStorage } from '@utils/storage';

type WalletState = {
  walletMap: WalletMap;
  addWallet: (email: string, wallet: Wallet, storeLocally?: boolean) => void;
  removeWallet: (email: string) => void;
  addWalletViaPassphrase: (email: string, passphrase: string, storeLocally?: boolean) => void;
  clearWalletMap: () => void;
};

const getInitialMap = (): WalletMap => {
  let data: WalletMap = {};

  const sessionData = getFromSessionStorage<WalletMap>('wallet-map');

  if (sessionData) {
    data = sessionData;
  } else {
    const localData = getFromLocalStorage<WalletMap>('wallet-map');

    if (localData) {
      data = localData;
    }

    setToSessionStorage(data, 'wallet-map');
  }

  if (typeof data === 'object' && data !== null && data?.liskAddress) {
    // Data is old wallet type used in versions before 1.2.0
    return {};
  }

  return data;
};

export const useWalletStore = create<WalletState>(set => ({
  walletMap: getInitialMap(),
  addWallet: (email, walletInput, storeLocally) =>
    set(state => {
      const wallet: Omit<Wallet, 'address'> = {
        lsk32address: walletInput.lsk32address,
        publicKey: walletInput.publicKey,
        passphrase: walletInput.passphrase,
      };

      const updatedMap = { ...state.walletMap };
      updatedMap[email] = wallet;

      setToSessionStorage(updatedMap, 'wallet-map');

      if (storeLocally) {
        setToLocalStorage(updatedMap, 'wallet-map');
      }

      return { ...state, walletMap: updatedMap };
    }),
  removeWallet: email =>
    set(state => {
      const updatedMap = { ...state.walletMap };
      delete updatedMap[email];

      setToSessionStorage(updatedMap, 'wallet-map');
      setToLocalStorage(updatedMap, 'wallet-map');

      return { ...state, walletMap: updatedMap };
    }),
  addWalletViaPassphrase: async (email, passphrase, storeLocally) => {
    const wallet = await generateWallet(passphrase);
    set(state => {
      const updatedMap = { ...state.walletMap };
      updatedMap[email] = wallet;

      setToSessionStorage(updatedMap, 'wallet-map');

      if (storeLocally) {
        setToLocalStorage(updatedMap, 'wallet-map');
      }

      return { ...state, walletMap: updatedMap };
    });
  },

  clearWalletMap: () =>
    set(() => {
      const emptyMap = {};

      setToSessionStorage(emptyMap, 'wallet-map');
      setToLocalStorage(emptyMap, 'wallet-map');

      return emptyMap;
    }),
}));
